import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DynamicTable from '../../Table';
import Avatar from "react-avatar"
import Icon from '../../../icons';
import { fetchUserDetail } from '_admin/features/users/usersAPI';
import { normalizeUserProfileResponse, isSuccessResponse, getErrorMessage } from '_admin/features/users/UserService';
import { jsonEncrypt } from '@common/util';

const licenseColumns = [
  { key: 'state', header: 'State' },
  { key: 'licenseNumber', header: 'License Number' },
  { key: 'expirationDate', header: 'License Expiration Date' },
];

const Profile = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedSupervisorName, setFetchedSupervisorName] = useState(null);

  const usersList = useSelector((state) => state.adminPanel?.usersData?.usersList);
  const orgName = useSelector((state) => state.sessionContextOidc?.organizationView?.OrganizationName);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    setFetchedSupervisorName(null);

    fetchUserDetail({ userId })
      .then((response) => {
        if (cancelled) return;
        if (isSuccessResponse(response)) {
          setProfile(normalizeUserProfileResponse(response));
        } else {
          setError(getErrorMessage(response, 'Failed to fetch user details'));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getErrorMessage(err, 'Failed to fetch user details'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const supervisorFromList = profile?.supervisorId
    ? (usersList || []).find((u) => String(u.id) === String(profile.supervisorId))
    : null;

  useEffect(() => {
    if (!profile?.supervisorId || supervisorFromList || profile?.supervisorName) return;

    let cancelled = false;
    fetchUserDetail({ userId: profile.supervisorId })
      .then((response) => {
        if (cancelled || !isSuccessResponse(response)) return;
        const supervisorDetail = normalizeUserProfileResponse(response);
        if (supervisorDetail?.fullName) {
          setFetchedSupervisorName(supervisorDetail.fullName);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [profile?.supervisorId, profile?.supervisorName, supervisorFromList]);

  const supervisorName = supervisorFromList
    ? (supervisorFromList.displayName || `${supervisorFromList.firstName} ${supervisorFromList.lastName}`.trim())
    : (profile?.supervisorName || fetchedSupervisorName);

  const handleViewSupervisor = () => {
    if (!profile?.supervisorId) return;
    navigate(`/user-profile/${jsonEncrypt(profile.supervisorId)}`);
  };

  return (
    <>
      <div className='colFlex admin__header'>
        <div className='rowFlex alignMiddle rowSpaceBetween mb'>
          <h1><a href="javascript:void(0)" onClick={onClose}>Users</a> / Profile</h1>
        </div>
      </div>

      {loading && (
        <div className='alignMiddle' style={{ padding: '24px', justifyContent: 'center', color: '#999' }}>Loading profile...</div>
      )}

      {!loading && error && (
        <div className='alignMiddle' style={{ padding: '24px', justifyContent: 'center', color: '#f44336' }}>{error}</div>
      )}

      {!loading && !error && profile && (
        <div className='admin__profile'>
          <div className='admin__profile__header rowFlex alignMiddle rowSpaceBetween mb'>
            <div className='rowFlex alignMiddle admin__profile__header__userprofile'>
              <Avatar
                round={true}
                size={"44"}
                name={profile.fullName}
                maxInitials={2}
                style={{ marginTop: '5px' }}
              />
              <h2>{profile.fullName}</h2>
            </div>
          </div>
          <div className='admin__profile__grid'>
            <ul>
              <li>
                <label>Full Name</label>
                <span>{profile.fullName}</span>
              </li>
              <li>
                <label>Job Title</label>
                <span>{profile.jobTitle || '-'}</span>
              </li>
              <li>
                <label>Phone Number</label>
                <span>{profile.phone || '-'}</span>
              </li>
              <li>
                <label>Email</label>
                <span>{profile.email || '-'}</span>
              </li>
              <li>
                <label>Supervisor</label>
                {supervisorName ? (
                  <span>
                    <a className='alignMiddle' href='javascript:void(0)' onClick={handleViewSupervisor}>
                      {supervisorName} <Icon name="NewPage" className="icon ml" />
                    </a>
                  </span>
                ) : (
                  <span>{profile.supervisorId ? 'Unknown' : '-'}</span>
                )}
              </li>
              <li>
                <label>Job Role</label>
                <span>{profile.jobRole.length ? profile.jobRole.join(', ') : '-'}</span>
              </li>
              <li>
                <label>Company Name</label>
                <span>{orgName || '-'}</span>
              </li>
            </ul>
          </div>
          {profile.licenses.length > 0 && (
            <div className='admin__profile__table'>
              <h3 className='mb'>Adjuster License Number ({profile.licenses.length})</h3>
              <DynamicTable columns={licenseColumns} data={profile.licenses} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Profile;
